import { Box, Heading, Link, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { QueryStatus } from 'react-query';

import Button from 'components/Button';
import { FormikInput, FormikSelect } from 'components/Formik';
import { companySizeOptions, industriesOptions } from 'containers/Auth/SignUp/option';

import { initialOrganizationDetailsFormData, organizationDetailsValidationSchema } from './schema';

interface IOrganizationDetails {
  handleOrganizationDetailsFormSubmit: (values: typeof initialOrganizationDetailsFormData) => void;
  status: QueryStatus;
  formData: typeof initialOrganizationDetailsFormData;
}

const OrganizationDetails: React.FC<IOrganizationDetails> = ({
  handleOrganizationDetailsFormSubmit,
  status,
  formData = initialOrganizationDetailsFormData,
}) => {
  const { t } = useTranslation(['auth', 'common']);
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      p={{
        base: '32px 16px',
        md: '60px',
        lg: '32px',
        xl: '60px',
        '2xl': '100px',
      }}
    >
      <Box maxWidth="700px">
        <Heading as="h2" size="3xl" mb="2">
          {t('auth:addYourOrganization')}
        </Heading>
        <Text fontSize="md" fontWeight="semibold" color="gray.500" mb="9">
          {t('auth:setUpSpace')}
        </Text>
        <Box maxWidth="500px" width="100%">
          <Formik
            initialValues={formData}
            validationSchema={organizationDetailsValidationSchema}
            onSubmit={handleOrganizationDetailsFormSubmit}
          >
            {() => (
              <Form noValidate>
                <FormikInput
                  type="text"
                  name="company"
                  label={t('auth:companyName')}
                  formControlProps={{ isRequired: true }}
                />
                <FormikInput
                  type="text"
                  name="phone"
                  label={t('auth:phoneNumber')}
                  formControlProps={{ isRequired: true }}
                />
                <FormikInput
                  type="text"
                  name="to_notes_1A4"
                  label={t('common:notes')}
                  inputProps={{ autoComplete: 'off', tabIndex: -1 }}
                  formControlProps={{
                    opacity: 0,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: 0,
                    width: 0,
                    zIndex: -1,
                  }}
                />
                <FormikSelect
                  name="industry"
                  label={t('auth:industry')}
                  options={industriesOptions}
                  formControlProps={{ isRequired: true }}
                  selectProps={{
                    placeholder: t('auth:selectYourIndustry'),
                    isClearable: false,
                  }}
                />
                <FormikSelect
                  name="company_size"
                  label={t('auth:howManyEmployeesWillBeUsingTimeero')}
                  options={companySizeOptions}
                  formControlProps={{ isRequired: true }}
                  selectProps={{
                    placeholder: t('auth:selectEmployeeCount'),
                    isClearable: false,
                  }}
                />
                <Text fontSize="sm" color="gray.500" mb={4}>
                  {t('auth:byCreatingAnAccountYouAreAgreeingToTheTimeero')}{' '}
                  <Link
                    href="https://timeero.com/terms-of-service"
                    target="_blank"
                    color="primary.500"
                    ml={1}
                    mr={1}
                    fontWeight={600}
                  >
                    {t('auth:termsOfService')}.
                  </Link>{' '}
                  {t('auth:weRespectYour')}
                  <Link
                    href="https://timeero.com/privacy-policy"
                    target="_blank"
                    color="primary.500"
                    ml={1}
                    fontWeight={600}
                  >
                    {t('auth:privacy')}.
                  </Link>
                </Text>
                <Button type="submit" colorScheme="secondary" width="100%" size="lg" isLoading={status === 'loading'}>
                  {t('auth:letsGo')}
                </Button>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationDetails;
